import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  hasMadeChoice,
  acceptAll,
  rejectAll,
  saveConsent,
  getConsent,
  CONSENT_EVENT,
} from "@/lib/consent";

/**
 * Slide-in bottom-right cookie consent banner. Renders on first visit only,
 * unless triggered via window.dispatchEvent(new Event('sm:reopen-cookies')).
 */
export default function CookieBanner() {
  const location = useLocation();
  const isOsRoute = location.pathname.startsWith("/os");
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ analytical: false, marketing: false });

  useEffect(() => {
    // Delay mount slightly so it doesn't compete with hero animations
    const t = setTimeout(() => {
      if (!hasMadeChoice()) setVisible(true);
    }, 600);
    const onReopen = () => {
      const c = getConsent();
      if (c) {
        setPrefs({ analytical: !!c.analytical, marketing: !!c.marketing });
        setShowDetails(true);
      }
      setVisible(true);
    };
    window.addEventListener("sm:reopen-cookies", onReopen);
    return () => {
      clearTimeout(t);
      window.removeEventListener("sm:reopen-cookies", onReopen);
    };
  }, []);

  useEffect(() => {
    const onChange = () => {/* keep mounted but hidden after choice */};
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };
  const handleSavePrefs = () => {
    saveConsent(prefs);
    setVisible(false);
  };

  if (!visible) return null;
  // Hide on internal OS routes to avoid overlapping the sidebar footer
  if (isOsRoute) return null;

  return (
    <div
      className="fixed left-4 bottom-4 md:left-6 md:bottom-6 md:max-w-sm z-[9999] animate-fade-in"
      data-testid="cookie-banner"
    >
      <div className="relative bg-[#0a0a0a]/95 backdrop-blur-xl border border-[#00E5FF]/20 rounded-2xl shadow-[0_0_40px_-15px_rgba(0,229,255,0.25)] overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent" />

        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
              <Cookie size={14} className="text-[#00E5FF]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white text-sm mb-1">
                Cookies
              </h3>
              <p className="text-[11px] text-zinc-400 leading-relaxed">
                Používame iba nevyhnutné + analytické cookies.{" "}
                <a
                  href="/cookies"
                  className="text-[#00E5FF] hover:underline"
                  data-testid="cookie-banner-cookies-link"
                >
                  Viac info
                </a>
                .
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowDetails((v) => !v)}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 transition-colors mb-3"
            data-testid="cookie-banner-toggle-details"
          >
            {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            Prispôsobiť
          </button>

          {showDetails && (
            <div className="space-y-2 mb-4 border-t border-white/5 pt-3" data-testid="cookie-banner-details">
              <CategoryRow
                title="Nevyhnutné"
                desc="Autentifikácia, bezpečnosť, session. Nedajú sa vypnúť."
                checked
                disabled
              />
              <CategoryRow
                title="Analytické"
                desc="Anonymné štatistiky návštevnosti (visitor_id, UTM, stránky)."
                checked={prefs.analytical}
                onChange={(v) => setPrefs((p) => ({ ...p, analytical: v }))}
                testid="cookie-cat-analytical"
              />
              <CategoryRow
                title="Marketingové"
                desc="Momentálne nepoužívame žiadne. Vyhradené pre budúce kampane."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testid="cookie-cat-marketing"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleRejectAll}
              className="py-2 px-3 text-xs rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 font-medium transition-colors"
              data-testid="cookie-banner-reject-all"
            >
              Iba nevyhnutné
            </button>
            {showDetails ? (
              <button
                onClick={handleSavePrefs}
                className="py-2 px-3 text-xs rounded-lg bg-[#00E5FF] hover:bg-[#33EEFF] text-black font-bold transition-colors"
                data-testid="cookie-banner-save-prefs"
              >
                Uložiť výber
              </button>
            ) : (
              <button
                onClick={handleAcceptAll}
                className="py-2 px-3 text-xs rounded-lg bg-[#00E5FF] hover:bg-[#33EEFF] text-black font-bold transition-colors"
                data-testid="cookie-banner-accept-all"
              >
                Prijať všetky
              </button>
            )}
          </div>
        </div>

        <button
          onClick={handleRejectAll}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          aria-label="Zavrieť a uložiť iba nevyhnutné"
          data-testid="cookie-banner-close"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

function CategoryRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <label
      className={`flex items-start gap-3 py-2 px-3 rounded-lg ${
        disabled ? "bg-white/[0.03]" : "bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        className="mt-0.5 w-4 h-4 accent-[#00E5FF] flex-shrink-0"
        checked={checked}
        disabled={disabled}
        onChange={disabled ? undefined : (e) => onChange(e.target.checked)}
        data-testid={testid}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
